import { ChangeDetectionStrategy, Component, computed, output, signal } from '@angular/core';
import { ANSWER_WORDS, VALID_GUESSES } from './wordle-words';

type LetterState = 'correct' | 'present' | 'absent' | 'empty';
type GameStatus = 'playing' | 'won' | 'lost';

interface Cell {
  letter: string;
  state: LetterState;
}

const WORD_LENGTH = 5;
const MAX_GUESSES = 6;


const KEYBOARD_ROWS: string[][] = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫']
];

/**
 * Hidden "WORDLE" easter egg, unlocked by typing "wordle" anywhere on the
 * page. Mirrors how SnakeGame is triggered, but is only reachable while
 * terminal mode is inactive (see App.handleGlobalKeydown).
 */
@Component({
  selector: 'app-wordle-game',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)'
  },
  template: `
    <div class="wordle-panel">
      <div class="wordle-header">
        <span class="wordle-title">WORDLE</span>
        <span class="wordle-status">{{ status() === 'playing' ? guesses().length + ' / ' + maxGuesses : (status() === 'won' ? 'SOLVED' : 'OUT OF GUESSES') }}</span>
        <button class="wordle-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <div class="wordle-grid">
        @for (row of rows(); track $index) {
          <div class="wordle-row">
            @for (cell of row; track $index) {
              <div class="wordle-cell" [class.wordle-cell-correct]="cell.state === 'correct'"
                   [class.wordle-cell-present]="cell.state === 'present'"
                   [class.wordle-cell-absent]="cell.state === 'absent'">
                {{ cell.letter }}
              </div>
            }
          </div>
        }
      </div>
      @if (message()) {
        <div class="wordle-message">{{ message() }}</div>
      } @else if (status() !== 'playing') {
        <div class="wordle-hint">PRESS ENTER TO PLAY AGAIN</div>
      } @else {
        <div class="wordle-hint">GUESS THE 5-LETTER WORD</div>
      }
      <div class="wordle-keyboard">
        @for (row of keyboardRows; track $index) {
          <div class="wordle-kb-row">
            @for (key of row; track key) {
              <button
                class="wordle-key"
                [class.wordle-key-wide]="key.length > 1"
                [class.wordle-key-correct]="keyboardStates()[key] === 'correct'"
                [class.wordle-key-present]="keyboardStates()[key] === 'present'"
                [class.wordle-key-absent]="keyboardStates()[key] === 'absent'"
                type="button"
                (click)="handleKeyLabel(key)">
                {{ key }}
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      height: 50vh;
      overflow: hidden;
      --wordle-size: clamp(18px, calc((50vh - 150px) / 9), 38px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.98);
      border-top: 2px solid var(--brand-line-active, #6e7ffb);
      font-family: var(--brand-font-base, Roboto, "Helvetica Neue", sans-serif);
      box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
    }
    .wordle-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }
    .wordle-header {
      display: flex;
      gap: 1.5rem;
      align-items: center;
      color: #1f2937;
      font-weight: 700;
      letter-spacing: 0.06em;
      font-size: 0.875rem;
    }
    .wordle-status {
      color: var(--brand-lotus-blue, #6e7ffb);
      font-weight: 600;
    }
    .wordle-close {
      background: none;
      border: 1px solid var(--brand-line-inactive, #ced3fa);
      color: #4b5563;
      padding: 0.25rem 0.6rem;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.75rem;
      border-radius: 6px;
    }
    .wordle-close:hover {
      background: var(--brand-lotus-blue, #6e7ffb);
      border-color: var(--brand-lotus-blue, #6e7ffb);
      color: #fff;
    }
    .wordle-grid {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    .wordle-row {
      display: flex;
      gap: 5px;
    }
    .wordle-cell {
      box-sizing: border-box;
      width: var(--wordle-size);
      height: var(--wordle-size);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid #d1d5db;
      font-weight: 700;
      font-size: 1.125rem;
      color: #1f2937;
      border-radius: 4px;
      background: #fff;
      transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }
    .wordle-cell-correct {
      background: #22c55e;
      border-color: #22c55e;
      color: #fff;
    }
    .wordle-cell-present {
      background: #eab308;
      border-color: #eab308;
      color: #fff;
    }
    .wordle-cell-absent {
      background: #6b7280;
      border-color: #6b7280;
      color: #fff;
    }
    .wordle-message {
      color: var(--brand-cta-primary, #ffcd3f);
      background: #1f2937;
      padding: 0.25rem 0.75rem;
      border-radius: 6px;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.03em;
    }
    .wordle-hint {
      color: #6b7280;
      font-size: 0.75rem;
      letter-spacing: 0.03em;
    }
    .wordle-keyboard {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    .wordle-kb-row {
      display: flex;
      gap: 5px;
      justify-content: center;
    }
    .wordle-key {
      min-width: 28px;
      height: var(--wordle-size);
      padding: 0 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 5px;
      background: #e5e7eb;
      color: #1f2937;
      font-weight: 600;
      font-size: 0.75rem;
      cursor: pointer;
      transition: background-color 0.15s ease, color 0.15s ease;
    }
    .wordle-key:hover {
      background: #d1d5db;
    }
    .wordle-key-wide {
      min-width: 48px;
      font-size: 0.625rem;
    }
    .wordle-key-correct {
      background: #22c55e;
      color: #fff;
    }
    .wordle-key-present {
      background: #eab308;
      color: #fff;
    }
    .wordle-key-absent {
      background: #6b7280;
      color: #fff;
    }
  `]
})
export class WordleGame {
  close = output<void>();

  readonly maxGuesses = MAX_GUESSES;
  readonly keyboardRows = KEYBOARD_ROWS;

  private answer = signal(this.pickRandomWord());
  guesses = signal<string[]>([]);
  currentGuess = signal('');
  status = signal<GameStatus>('playing');
  message = signal('');

  private messageTimeoutId?: ReturnType<typeof setTimeout>;

  rows = computed<Cell[][]>(() => {
    const answer = this.answer();
    const guesses = this.guesses();
    const current = this.currentGuess();
    const output: Cell[][] = [];

    for (let i = 0; i < MAX_GUESSES; i++) {
      if (i < guesses.length) {
        output.push(this.evaluateGuess(guesses[i], answer));
      } else if (i === guesses.length && this.status() === 'playing') {
        output.push(this.buildRow(current));
      } else {
        output.push(this.buildRow(''));
      }
    }
    return output;
  });

  keyboardStates = computed<Record<string, LetterState>>(() => {
    const answer = this.answer();
    const states: Record<string, LetterState> = {};
    const rank: Record<LetterState, number> = { empty: 0, absent: 1, present: 2, correct: 3 };

    for (const guess of this.guesses()) {
      const evaluated = this.evaluateGuess(guess, answer);
      evaluated.forEach(cell => {
        const key = cell.letter.toUpperCase();
        if (rank[cell.state] > rank[states[key] ?? 'empty']) {
          states[key] = cell.state;
        }
      });
    }
    return states;
  });

  requestClose(): void {
    this.close.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.status() !== 'playing') {
      if (event.key === 'Enter') {
        this.restart();
      } else if (event.key === 'Escape') {
        this.requestClose();
      }
      return;
    }

    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }

    this.processKey(event.key);
  }

  handleKeyLabel(label: string): void {
    if (this.status() !== 'playing') {
      this.restart();
      return;
    }
    if (label === 'ENTER') {
      this.processKey('Enter');
    } else if (label === '⌫') {
      this.processKey('Backspace');
    } else {
      this.processKey(label);
    }
  }

  private processKey(key: string): void {
    if (key === 'Backspace') {
      this.currentGuess.update(current => current.slice(0, -1));
      return;
    }
    if (key === 'Enter') {
      this.submitGuess();
      return;
    }
    if (/^[a-zA-Z]$/.test(key) && this.currentGuess().length < WORD_LENGTH) {
      this.currentGuess.update(current => current + key.toLowerCase());
    }
  }

  private submitGuess(): void {
    const guess = this.currentGuess();
    if (guess.length !== WORD_LENGTH) {
      this.flashMessage('NOT ENOUGH LETTERS');
      return;
    }
    if (!VALID_GUESSES.has(guess)) {
      this.flashMessage('NOT IN WORD LIST');
      return;
    }

    const nextGuesses = [...this.guesses(), guess];
    this.guesses.set(nextGuesses);
    this.currentGuess.set('');

    if (guess === this.answer()) {
      this.status.set('won');
    } else if (nextGuesses.length >= MAX_GUESSES) {
      this.status.set('lost');
      this.flashMessage(`THE WORD WAS ${this.answer().toUpperCase()}`, true);
    }
  }

  private restart(): void {
    this.answer.set(this.pickRandomWord());
    this.guesses.set([]);
    this.currentGuess.set('');
    this.status.set('playing');
    this.message.set('');
  }

  private flashMessage(text: string, persist = false): void {
    this.message.set(text);
    if (this.messageTimeoutId !== undefined) {
      clearTimeout(this.messageTimeoutId);
      this.messageTimeoutId = undefined;
    }
    if (!persist) {
      this.messageTimeoutId = setTimeout(() => this.message.set(''), 1200);
    }
  }

  private evaluateGuess(guess: string, answer: string): Cell[] {
    const result: LetterState[] = new Array(WORD_LENGTH).fill('absent');
    const answerChars = answer.split('');
    const guessChars = guess.split('');
    const used = new Array(WORD_LENGTH).fill(false);

    guessChars.forEach((char, i) => {
      if (char === answerChars[i]) {
        result[i] = 'correct';
        used[i] = true;
      }
    });

    guessChars.forEach((char, i) => {
      if (result[i] === 'correct') {
        return;
      }
      const idx = answerChars.findIndex((a, j) => a === char && !used[j]);
      if (idx !== -1) {
        result[i] = 'present';
        used[idx] = true;
      }
    });

    return guessChars.map((char, i) => ({ letter: char.toUpperCase(), state: result[i] }));
  }

  private buildRow(partial: string): Cell[] {
    return Array.from({ length: WORD_LENGTH }, (_, i) => ({
      letter: (partial[i] ?? '').toUpperCase(),
      state: 'empty' as LetterState
    }));
  }

  private pickRandomWord(): string {
    return ANSWER_WORDS[Math.floor(Math.random() * ANSWER_WORDS.length)];
  }
}
